import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Castle, Dice1, Users, Search, Filter, Clock, User, LogIn, LogOut, ChevronDown, Check, Camera, Trophy, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ThemedBackground } from '../components/ThemedBackground';
import { supabase, waitForSupabaseAuthReady } from '../lib/supabase';
import NicknameLoginModal from '../components/NicknameLoginModal';
import ConfirmDialog from '../components/ConfirmDialog';
import CreateRoomModal from '../components/CreateRoomModal';
import { auth } from '../lib/auth';

interface Room {
  id: number;
  room_id: string;
  room_name: string;
  host_player_id: number;
  game_mode: string;
  max_players: number;
  min_players: number;
  is_private: boolean;
  status: 'waiting' | 'full' | 'playing';
  current_round: number;
  created_by_nickname: string;
  created_at: string;
  player_count?: number;
}

interface UserProfile {
  nickname: string;
  avatar: string;
}

interface PlayerStats {
  totalGames: number;
  wins: number;
  losses: number;
  winRate: number;
}

export default function OnlineLobby() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserProfile>({ nickname: '', avatar: '' });
  const [userStats, setUserStats] = useState<PlayerStats | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'waiting' | 'full' | 'playing'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  
  // 登录退出弹窗状态
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  
  // 加入房间密码输入对话框
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingJoinRoom, setPendingJoinRoom] = useState<{ id: string; room: Room } | null>(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  
  // 加入身份选择（玩家/观战者）
  const [showRoleSelectModal, setShowRoleSelectModal] = useState(false);
  const [pendingJoinAsSpectator, setPendingJoinAsSpectator] = useState(false);

  const bucketName = 'Tenant-dinga3c67f044ba91612a39a90f97fcb1e09';

  const AVATAR_OPTIONS = [
    '👤', '', '👨', '👩', '🧔', '', '🧓', '👴',
    '👵', '', '👮', '👷', '💂', '️', '🏃', '🧘',
  ];

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
    };
    if (showStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusDropdown]);

  useEffect(() => {
    checkAuth();
    
    // 监听登录状态变化
    const unsubscribe = auth.subscribe((user) => {
      setIsLoggedIn(!!user);
      if (user) {
        setCurrentUser({ nickname: user.nickname, avatar: user.avatar });
        loadUserStats(user.nickname);
      } else {
        setCurrentUser({ nickname: '', avatar: '' });
        setUserStats(null);
      }
    });
    
    return unsubscribe;
  }, []);

  const loadUserStats = async (nickname: string) => {
    if (!nickname) {
      setUserStats(null);
      return;
    }

    try {
      await waitForSupabaseAuthReady();
      const allGamesData = await import('../modules/stats/dataService').then(m => m.statsDataService.loadHistoryGames('all', 'all'));
      
      const playerGames = allGamesData.data.filter(game => 
        game.players?.some(p => p.player_nickname === nickname)
      );
      
      if (playerGames.length === 0) {
        setUserStats({ totalGames: 0, wins: 0, losses: 0, winRate: 0 });
        return;
      }

      const playerRecordsMap = new Map<string, any>();
      allGamesData.data.forEach(game => {
        const player = game.players?.find(p => p.player_nickname === nickname);
        if (player) {
          const uniqueKey = `${player.player_nickname}_${player.game_record_id || game.id}`;
          if (!playerRecordsMap.has(uniqueKey)) {
            playerRecordsMap.set(uniqueKey, player);
          }
        }
      });
      
      const playerRecords = Array.from(playerRecordsMap.values());
      
      let wins = 0;
      let losses = 0;
      
      playerRecords.forEach(record => {
        const isWin = (record.score_change || 0) > 0;
        if (isWin) {
          wins++;
        } else {
          losses++;
        }
      });
      
      const totalGames = playerRecords.length;
      const winRate = totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0;

      setUserStats({ totalGames, wins, losses, winRate });
    } catch (err) {
      console.error('[OnlineLobby] 加载战绩数据失败:', err);
      setUserStats({ totalGames: 0, wins: 0, losses: 0, winRate: 0 });
    }
  };

  const checkAuth = async () => {
    await waitForSupabaseAuthReady();
    const user = auth.getCurrentUser();
    setIsLoggedIn(!!user);
    await fetchRooms();
    setLoading(false);
  };

  const fetchRooms = async () => {
    try {
      await waitForSupabaseAuthReady();
      
      // 1. 查询所有未删除、未取消的房间
      let query = supabase
        .from('online_game_rooms_cce0d86f_8f5')
        .select(`
          *,
          players:online_room_players_cce0d86f_8f5 (
            id,
            player_id,
            is_host
          )
        `)
        .eq('is_deleted', 'n')
        .neq('status', 'cancelled')
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[fetchRooms] 查询失败:', error);
        return;
      }

      // 2. 过滤掉房主不在线的房间
      const activeRooms = data.filter(room => {
        // 检查房主是否还在房间玩家列表中
        const hostInRoom = room.players?.some((p: any) => p.is_host) || false;
        return hostInRoom;
      });

      const roomsWithCount = activeRooms.map(room => ({
        ...room,
        player_count: room.players?.length || 0,
      }));
      setRooms(roomsWithCount);
    } catch (err) {
      console.error('[fetchRooms] 异常:', err);
    }
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    auth.logout();
    setIsLoggedIn(false);
    setCurrentUser({ nickname: '', avatar: '' });
    setUserStats(null);
    setShowLogoutConfirm(false);
  };

  const handleAvatarUploadClick = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const fileName = `public/avatars/${Date.now()}_${file.name}`;
      
      const { error } = await supabase.storage
        .from(bucketName)
        .upload(fileName, arrayBuffer, {
          contentType: file.type,
          upsert: false,
        });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(fileName);

      const newAvatarUrl = urlData.publicUrl;
      const user = auth.getCurrentUser();
      if (user) {
        auth.updateUser({ avatar: newAvatarUrl });
      }
      setCurrentUser(prev => ({ ...prev, avatar: newAvatarUrl }));
    } catch (error) {
      console.error('头像上传失败:', error);
      alert('头像上传失败，请重试');
    } finally {
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };

  const handleSelectAvatar = (avatar: string) => {
    const user = auth.getCurrentUser();
    if (user) {
      auth.updateUser({ avatar });
    }
    setCurrentUser(prev => ({ ...prev, avatar }));
    setShowAvatarSelector(false);
  };

  const handleQuickMatch = () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    alert('快速匹配功能开发中...');
  };

  const handleCreateRoom = () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    setShowCreateRoomModal(true);
  };

  const handleCreateRoomSuccess = (roomId: string) => {
    navigate(`/online/room/${roomId}`);
  };

  const handleJoinRoom = async (roomId: string) => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    
    // 找到房间信息 - 支持 room_id (UUID 字符串) 或 id (数字字符串)
    const room = rooms.find(r => r.room_id === roomId || r.id.toString() === roomId);
    if (!room) {
      alert('房间不存在');
      return;
    }
    
    // 弹出身份选择对话框
    setPendingJoinRoom({ id: roomId, room });
    setPendingJoinAsSpectator(false);
    setPasswordInput('');
    setShowRoleSelectModal(true);
  };
  
  const handleSelectRole = async (asSpectator: boolean) => {
    setShowRoleSelectModal(false);
    setPendingJoinAsSpectator(asSpectator);
    
    // 如果是私密房，弹出密码输入框
    if (pendingJoinRoom?.room.is_private) {
      setPasswordInput('');
      setShowPasswordModal(true);
      return;
    }
    
    // 公开房直接加入
    await joinRoom(pendingJoinRoom!.id, null, asSpectator);
  };
  
  const joinRoom = async (roomId: string, password: string | null, asSpectator: boolean = false) => {
    if (!currentUser.nickname) {
      setShowLoginModal(true);
      return;
    }
    
    // 找到房间信息 - 支持 room_id (UUID 字符串) 或 id (数字字符串)
    const room = rooms.find(r => r.room_id === roomId || r.id.toString() === roomId);
    if (!room) {
      alert('房间不存在');
      return;
    }
    
    setJoinLoading(true);
    try {
      await waitForSupabaseAuthReady();
      
      const userProfile = auth.getCurrentUser();
      if (!userProfile) {
        alert('请先登录');
        setJoinLoading(false);
        return;
      }
      
      let result;
      
      // 使用房间的 numeric id，而不是 URL 中的 roomId 字符串
      const numericRoomId = room.id;
      
      if (asSpectator) {
        // 获取玩家 ID
        const { data: playerProfile } = await supabase.rpc(
          'get_or_create_player_profile',
          { p_nickname: currentUser.nickname }
        );
        
        if (!playerProfile) {
          alert('获取玩家资料失败');
          setJoinLoading(false);
          return;
        }
        
        const playerId = playerProfile as number;
        
        const { data, error } = await supabase.rpc('join_as_spectator', {
          p_room_id: numericRoomId,
          p_player_id: playerId,
          p_nickname: currentUser.nickname,
          p_password: password
        });
        
        if (error) {
          console.error('加入房间失败:', error);
          alert('加入房间失败：' + error.message);
          setJoinLoading(false);
          return;
        }
        
        result = data as any;
        if (!result.success) {
          alert(result.error || '加入房间失败');
          setJoinLoading(false);
          return;
        }
      } else {
        // 获取玩家 ID
        const { data: playerProfile } = await supabase.rpc(
          'get_or_create_player_profile',
          { p_nickname: currentUser.nickname }
        );
        
        if (!playerProfile) {
          alert('获取玩家资料失败');
          setJoinLoading(false);
          return;
        }
        
        const playerId = playerProfile as number;
        
        const { data, error } = await supabase.rpc('join_online_room', {
          p_room_id: numericRoomId,
          p_player_id: playerId,
          p_nickname: currentUser.nickname,
          p_password: password
        });
        
        if (error) {
          console.error('加入房间失败:', error);
          alert('加入房间失败：' + error.message);
          setJoinLoading(false);
          return;
        }
        
        result = data as any;
        if (!result.success) {
          alert(result.error || '加入房间失败');
          setJoinLoading(false);
          return;
        }
      }
      
      // 成功加入，跳转到房间页面 - 使用房间的 numeric id
      navigate(`/online/room/${numericRoomId}`);
    } catch (err) {
      console.error('加入房间异常:', err);
      alert('加入房间失败，请稍后重试');
    } finally {
      setJoinLoading(false);
    }
  };
  
  const handlePasswordSubmit = async () => {
    if (!pendingJoinRoom) return;
    if (!passwordInput.trim()) {
      alert('请输入密码');
      return;
    }
    await joinRoom(pendingJoinRoom.id, passwordInput.trim(), pendingJoinAsSpectator);
  };

  const filteredRooms = rooms.filter(room => {
    const matchesSearch = room.room_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         room.created_by_nickname.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getStatusBadge = (status: string, playerCount: number, maxPlayers: number) => {
    if (status === 'playing') {
      return (
        <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/30 text-red-400 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          游戏中
        </span>
      );
    }
    if (playerCount >= maxPlayers) {
      return (
        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/30 text-amber-400 flex items-center gap-1">
          <Users className="w-3 h-3" />
          已满
        </span>
      );
    }
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/30 text-green-400 flex items-center gap-1">
        <Users className="w-3 h-3" />
        等待中
      </span>
    );
  };

  return (
    <ThemedBackground pageName="online-lobby">
      <div className="min-h-screen p-5 pt-[72px] pb-8">
        {/* 固定顶部导航栏 */}
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-4 px-5 py-2" style={{ backgroundColor: '#1A0F2A', borderBottom: '1px solid #5A4BFF35' }}>
          <button
            onClick={() => navigate('/')}
            className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-white hover:bg-avalon-purple transition-colors"
            title="返回首页"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">线上游戏</h1>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-avalon-text hover:text-white hover:bg-avalon-purple transition-colors"
            title="刷新页面"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
        </div>

        {/* 登录状态卡片 - 始终显示 */}
        <div className="mb-6 rounded-xl p-3 border transition-all bg-yellow-500/20 border-yellow-500/40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                {currentUser.avatar && (currentUser.avatar.startsWith('http://') || currentUser.avatar.startsWith('https://')) ? (
                  <img 
                    src={currentUser.avatar}
                    alt="avatar"
                    className="w-10 h-10 rounded-full object-cover cursor-pointer ring-2 ring-white/20 hover:ring-green-400/50 transition-all"
                    onClick={() => isLoggedIn && setShowAvatarSelector(true)}
                  />
                ) : (
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-2xl cursor-pointer ring-2 ring-white/20 hover:ring-green-400/50 transition-all bg-avalon-card"
                    onClick={() => isLoggedIn && setShowAvatarSelector(true)}
                  >
                    {currentUser.avatar || '👤'}
                  </div>
                )}
                {isLoggedIn && (
                  <>
                    <button
                      onClick={() => setShowAvatarSelector(true)}
                      className="absolute bottom-0 right-0 w-5 h-5 rounded-full flex items-center justify-center shadow-lg bg-avalon-gold hover:bg-yellow-400 transition-colors"
                      title="更换头像"
                    >
                      <Camera className="w-2.5 h-2.5 text-avalon-bg-dark" />
                    </button>
                    <input
                      type="file"
                      ref={avatarInputRef}
                      onChange={handleAvatarUpload}
                      accept="image/*"
                      className="hidden"
                    />
                  </>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-white font-semibold text-base">
                    {isLoggedIn ? (currentUser.nickname || '未设置昵称') : '未登录'}
                  </h3>
                  {isLoggedIn && userStats && (
                    <button
                      onClick={() => navigate(`/player-detail?nickname=${encodeURIComponent(currentUser.nickname)}`)}
                      className="px-2 py-0.5 rounded-lg bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs hover:bg-blue-500/30 transition-colors"
                    >
                      查看明细战绩
                    </button>
                  )}
                </div>
                {isLoggedIn && userStats && (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center gap-1 text-xs">
                      <Trophy className="w-3 h-3 text-avalon-gold" />
                      <span className="text-green-300 font-medium">{userStats.wins}</span>
                      <span className="text-avalon-text">胜</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-red-300 font-medium">{userStats.losses}</span>
                      <span className="text-avalon-text">负</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-avalon-text">总场次</span>
                      <span className="text-white font-medium">{userStats.totalGames}</span>
                    </div>
                    <span className="text-xs text-green-300">
                      胜率 {userStats.winRate}%
                    </span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => isLoggedIn ? handleLogout() : setShowLoginModal(true)}
              className="w-9 h-9 rounded-xl flex items-center justify-center transition-all bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 border border-yellow-400/30"
              title={isLoggedIn ? '退出登录' : '登录'}
            >
              {isLoggedIn ? (
                <LogOut className="w-4 h-4" />
              ) : (
                <LogIn className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* 主要功能按钮 */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <button
            onClick={handleCreateRoom}
            disabled={!isLoggedIn}
            className={`group py-8 rounded-xl font-semibold text-lg transition-all transform hover:scale-105 active:scale-95 ${
              isLoggedIn
                ? 'bg-red-600/30 text-white border-2 border-red-500/50 hover:border-red-400'
                : 'bg-gray-500/20 text-gray-400 border border-gray-500/30 cursor-not-allowed'
            }`}
          >
            <div className="flex flex-col items-center gap-2">
              <Castle className="w-8 h-8 text-red-400 group-hover:text-red-300 transition-colors" />
              <span className="drop-shadow-lg">创建房间</span>
              <span className="text-xs opacity-80 font-normal">自建私密/公开房</span>
            </div>
          </button>

          <button
            onClick={handleQuickMatch}
            disabled={!isLoggedIn}
            className={`group py-8 rounded-xl font-semibold text-lg transition-all transform hover:scale-105 active:scale-95 ${
              isLoggedIn
                ? 'bg-green-600/30 text-white border-2 border-green-500/50 hover:border-green-400'
                : 'bg-gray-500/20 text-gray-400 border border-gray-500/30 cursor-not-allowed'
            }`}
          >
            <div className="flex flex-col items-center gap-2">
              <Dice1 className="w-8 h-8 text-green-400 group-hover:text-green-300 transition-colors" />
              <span className="drop-shadow-lg">快速匹配</span>
              <span className="text-xs opacity-80 font-normal">自动匹配加入房间</span>
            </div>
          </button>
        </div>

        {/* 房间列表区域 */}
        <div className="bg-avalon-card border border-avalon-purple/20 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-avalon-gold" />
              房间列表
            </h3>
            <span className="text-sm text-avalon-text">{filteredRooms.length} 个房间</span>
          </div>

          {/* 搜索和筛选 */}
          <div className="flex gap-2 mb-3">
            <div className="flex-1 relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-avalon-text" />
              <input
                type="text"
                placeholder="搜索房间或房主..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 rounded-lg bg-avalon-bg-dark border border-avalon-purple/30 text-white text-sm placeholder-avalon-text focus:outline-none focus:border-avalon-gold transition-colors"
              />
            </div>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className="flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-lg bg-avalon-bg-dark border border-avalon-purple/30 text-white text-sm hover:border-avalon-gold/50 transition-colors"
              >
                <span>{statusFilter === 'all' ? '全部状态' : statusFilter === 'waiting' ? '等待中' : statusFilter === 'full' ? '已满' : '游戏中'}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* 下拉菜单 */}
              {showStatusDropdown && (
                <div className="absolute right-0 mt-2 w-32 bg-avalon-card border border-avalon-purple/30 rounded-lg shadow-xl z-50 overflow-hidden">
                  <button
                    onClick={() => { setStatusFilter('all'); setShowStatusDropdown(false); }}
                    className="w-full px-3 py-2 text-left text-white text-sm hover:bg-avalon-purple/30 flex items-center justify-between transition-colors"
                  >
                    <span>全部状态</span>
                    {statusFilter === 'all' && <Check className="w-3.5 h-3.5 text-avalon-gold" />}
                  </button>
                  <div className="h-px bg-avalon-purple/20" />
                  <button
                    onClick={() => { setStatusFilter('waiting'); setShowStatusDropdown(false); }}
                    className="w-full px-3 py-2 text-left text-white text-sm hover:bg-avalon-purple/30 flex items-center justify-between transition-colors"
                  >
                    <span>等待中</span>
                    {statusFilter === 'waiting' && <Check className="w-3.5 h-3.5 text-avalon-gold" />}
                  </button>
                  <div className="h-px bg-avalon-purple/20" />
                  <button
                    onClick={() => { setStatusFilter('full'); setShowStatusDropdown(false); }}
                    className="w-full px-3 py-2 text-left text-white text-sm hover:bg-avalon-purple/30 flex items-center justify-between transition-colors"
                  >
                    <span>已满</span>
                    {statusFilter === 'full' && <Check className="w-3.5 h-3.5 text-avalon-gold" />}
                  </button>
                  <div className="h-px bg-avalon-purple/20" />
                  <button
                    onClick={() => { setStatusFilter('playing'); setShowStatusDropdown(false); }}
                    className="w-full px-3 py-2 text-left text-white text-sm hover:bg-avalon-purple/30 flex items-center justify-between transition-colors"
                  >
                    <span>游戏中</span>
                    {statusFilter === 'playing' && <Check className="w-3.5 h-3.5 text-avalon-gold" />}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 房间列表 */}
          {loading ? (
            <div className="text-center py-12 text-avalon-text">加载中...</div>
          ) : filteredRooms.length === 0 ? (
            <div className="text-center py-12 text-avalon-text">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>暂无房间</p>
              <p className="text-sm mt-1">创建一个房间开始游戏吧</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredRooms.map((room) => (
                <div
                  key={room.id}
                  onClick={() => handleJoinRoom(room.room_id)}
                  className="group p-3 rounded-lg bg-avalon-bg-dark/50 border border-avalon-purple/20 hover:border-avalon-gold/50 transition-all cursor-pointer hover:transform hover:scale-[1.02]"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/30 text-blue-400">
                          {room.id}号房间
                        </span>
                        <h4 className="text-white font-medium text-sm">{room.room_name}</h4>
                        {room.is_private && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/30 text-purple-400">
                            私密
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-avalon-text">
                        {getStatusBadge(room.status, room.player_count || 0, room.max_players)}
                        <span className="flex items-center gap-1 truncate">
                          <User className="w-3.5 h-3.5" />
                          {room.created_by_nickname}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {room.player_count || 0}/{room.max_players}
                        </span>
                      </div>
                    </div>
                    <div className="ml-3 flex-shrink-0">
                      <button
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          room.status === 'waiting' && (room.player_count || 0) < room.max_players
                            ? 'bg-avalon-gold text-avalon-bg-dark hover:bg-yellow-400'
                            : 'bg-gray-500/30 text-gray-400 cursor-not-allowed'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (room.status === 'waiting' && (room.player_count || 0) < room.max_players) {
                            handleJoinRoom(room.room_id);
                          }
                        }}
                      >
                        {(room.player_count || 0) >= room.max_players ? '已满' : '加入'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 登录弹窗 */}
      <NicknameLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={() => {
          setShowLoginModal(false);
        }}
      />
      
      {/* 退出确认弹窗 */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        title="确认退出登录"
        message="确定要退出当前账号吗？"
        confirmText="退出"
        cancelText="取消"
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />

      {/* 创建房间弹窗 */}
      <CreateRoomModal
        isOpen={showCreateRoomModal}
        onClose={() => setShowCreateRoomModal(false)}
        onCreateSuccess={handleCreateRoomSuccess}
      />

      {/* 头像选择弹窗 */}
      {showAvatarSelector && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowAvatarSelector(false)}
        >
          <div
            className="rounded-2xl p-6 max-w-md w-full max-h-[70vh] overflow-y-auto avalon-card bg-avalon-card border border-avalon-purple/30"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">选择头像</h3>
              <button
                onClick={() => setShowAvatarSelector(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-avalon-purple/20 transition-colors"
              >
                <svg className="w-5 h-5 text-avalon-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {AVATAR_OPTIONS.map((avatar) => (
                <button
                  key={avatar || 'default'}
                  onClick={() => handleSelectAvatar(avatar)}
                  className={`w-14 h-14 rounded-xl flex items-center justify-center text-3xl transition-all hover:scale-110 avalon-card ${
                    currentUser.avatar === avatar ? 'ring-2 ring-avalon-gold' : ''
                  }`}
                >
                  {avatar || '👤'}
                </button>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-avalon-purple/20">
              <button
                onClick={handleAvatarUploadClick}
                className="w-full py-3 rounded-lg bg-avalon-gold/20 border border-avalon-gold/30 text-avalon-gold hover:bg-avalon-gold/30 transition-colors flex items-center justify-center gap-2"
              >
                <Camera className="w-4 h-4" />
                上传自定义头像
              </button>
              <input
                type="file"
                ref={avatarInputRef}
                onChange={handleAvatarUpload}
                accept="image/*"
                className="hidden"
              />
            </div>
          </div>
        </div>
      )}

      {/* 加入身份选择对话框 */}
      {showRoleSelectModal && pendingJoinRoom && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowRoleSelectModal(false)}
        >
          <div
            className="rounded-2xl p-6 max-w-md w-full avalon-card bg-avalon-card border border-avalon-purple/30"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">选择加入方式</h3>
              <button
                onClick={() => setShowRoleSelectModal(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-avalon-purple/20 transition-colors"
              >
                <svg className="w-5 h-5 text-avalon-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <p className="text-avalon-text mb-1">房间：{pendingJoinRoom.room.room_name}</p>
              <p className="text-sm text-avalon-text/70">请选择您要加入的身份</p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => handleSelectRole(false)}
                disabled={joinLoading}
                className="w-full px-4 py-4 rounded-xl bg-avalon-gold/20 border border-avalon-gold/50 text-avalon-gold font-medium hover:bg-avalon-gold/30 transition-all disabled:opacity-50 flex items-center gap-3"
              >
                <span className="text-2xl">🎮</span>
                <div className="text-left">
                  <div className="text-base">作为玩家加入</div>
                  <div className="text-xs opacity-70">参与游戏，执行任务</div>
                </div>
              </button>

              <button
                onClick={() => handleSelectRole(true)}
                disabled={joinLoading}
                className="w-full px-4 py-4 rounded-xl bg-avalon-purple/20 border border-avalon-purple/50 text-avalon-purple font-medium hover:bg-avalon-purple/30 transition-all disabled:opacity-50 flex items-center gap-3"
              >
                <span className="text-2xl">👁️</span>
                <div className="text-left">
                  <div className="text-base">作为观战者加入</div>
                  <div className="text-xs opacity-70">观看游戏，不参与投票</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 加入私密房密码输入对话框 */}
      {showPasswordModal && pendingJoinRoom && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowPasswordModal(false)}
        >
          <div
            className="rounded-2xl p-6 max-w-md w-full avalon-card bg-avalon-card border border-avalon-purple/30"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">加入私密房间</h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-avalon-purple/20 transition-colors"
              >
                <svg className="w-5 h-5 text-avalon-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-avalon-text mb-2">房间：{pendingJoinRoom.room.room_name}</p>
              <p className="text-sm text-avalon-text/70">请输入房间密码</p>
            </div>

            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !joinLoading) {
                  handlePasswordSubmit();
                }
              }}
              placeholder="房间密码"
              className="w-full px-4 py-3 rounded-xl bg-avalon-bg border border-avalon-purple/30 text-white placeholder-avalon-text/40 focus:outline-none focus:border-avalon-gold/50 focus:ring-2 focus:ring-avalon-gold/20 transition-all mb-4"
              autoFocus
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowPasswordModal(false)}
                disabled={joinLoading}
                className="flex-1 px-4 py-3 rounded-xl border border-avalon-purple/30 text-avalon-text hover:bg-avalon-purple/20 transition-colors disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={handlePasswordSubmit}
                disabled={joinLoading || !passwordInput.trim()}
                className="flex-1 px-4 py-3 rounded-xl bg-avalon-gold text-avalon-bg-dark font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {joinLoading ? '加入中...' : '加入房间'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ThemedBackground>
  );
}
